import {
  handleHubspotChatLoad,
  identifyUserOnHubSpot,
  loadHubspotScript,
  removeHubSpotChat,
} from "@/helpers/hubspot";
import { mergeChildren } from "@/helpers/services/filters";
import {
  checkExpiredDate,
  isTokenExpired,
  removeMask,
} from "@/helpers/services/utils.js";
import {
  BASIC_SKU,
  ENTERPRISE_SKU,
  STATUS,
  SUBSCRIPTIONS,
  partnerType,
} from "@/helpers/variables/backendConstants";
import { get, getDatabase, ref } from "firebase/database";

function handleDisabledHubspotChat(commit) {
  if (typeof window === "undefined") return false;
  if (window.__HUBSPOT_CHAT_DISABLED) {
    if (commit && typeof commit === "function") {
      commit("setChatLoading", false);
      commit("setOpenModalSupport", true);
    }
    return true;
  }
  return false;
}

const state = () => ({
  company: null,
  control: null,
  products: [],
  plans: [],
  services: [],
  missingScopesSuite: [],
  loadingClientPlans: false,
  loadedClientPlans: false,
  uos: [],
  IsScheduleMeetingSnackbarVisible: false,
  actionsScheduleDialog: false,
  actionsScheduleType: "help",
  groups: [],
  functionalityClicked: undefined,
  companySharedDrives: [],
  openModalSupport: false,
  msRolesConfirmed: null,
  domainLicenses: {
    items: {},
    header_licenses: [],
  },
  loadingDomainLicenses: false,
});

const getters = {
  isVisibleModalSupport: (state) => state.openModalSupport,
  hasTag: (state) => (tag) => {
    const tags = state.company ? state.company.tag : [];
    return tags.includes(tag);
  },

  isDashTester: (state, getters) => getters.hasTag("dashTester"),
  isBetaTesterMassEmailMigration: (state, getters) =>
    getters.hasTag("BetaTesterMassEmailMigration"),
  isSmallClient: (state) => state.company.users_number <= 5,
  isUpTo15Users: (state) => state.company.users_number <= 15,
  groupEmails: (state) => state.groups,
  getActionsScheduleType: (state) => state.actionsScheduleType,
  isReseller: (state) => state.company && state.company.is_reseller,
  responsibleEmail: (state) => state.company.responsible_email,
  mainDomain: (state) => state.company?.main_domain,
  domains: (state) => state.company.domains_list,
  company: (state) => state.company,
  usersNumber: (state) => state.company.users_number,
  hasIntermediaryPartner: (state) =>
    state.company?.reseller_company &&
    state.company.reseller_company.type === partnerType.INTERMEDIARY,
  resellerCompanyName: (state) => state.company?.reseller_company?.name,
  companyFreshChatConfig: (state) => {
    const company = state.company || {};
    return {
      domain: company.main_domain,
      responsible_email: company.responsible_email,
      name: company.name,
    };
  },
  IsScheduleMeetingSnackbarVisible: (state) =>
    state.IsScheduleMeetingSnackbarVisible,
  isConectaControlClient: (state) => state.control,
  isConecta: (state) =>
    state.company?.main_domain
      ? state.company.main_domain === "conectanuvem.com.br"
      : false,
  isDevConecta: (state) =>
    state.company?.main_domain
      ? state.company.main_domain === "dev.conectanuvem.com.br"
      : false,
  isCompanyMicrosoft: (state) => state.company?.oauth === "microsoft",
  serviceProvider: (state) => state.company.oauth || "google",
  contractedProducts: (state) => state.products,
  trialPlans: (state) =>
    state.plans.filter((plan) => plan.status === STATUS.TRIAL) || [],
  hasTrialPlans: (state) => {
    const companyPlans = state.plans || [];
    return (
      companyPlans.filter(
        (plan) =>
          plan.valid_status === STATUS.TRIAL &&
          plan.subscription_type === SUBSCRIPTIONS.TRIAL
      ).length > 0
    );
  },
  contractedPlans: (state) => state.plans,
  hasContractedPlans: (state, getters) =>
    getters.contractedPlans.length > 0 &&
    getters.contractedPlans.some(
      (plan) => STATUS.ACTIVE === plan.status && plan.plan.rank > 0
    ),
  missingScopesSuite: (state) => state.missingScopesSuite,
  loadingClientPlans: (state) => state.loadingClientPlans,
  hasAcceptedTerms: (state) => {
    return Object.keys(state.company.reseller_accepted_terms).length > 0;
  },
  companyHasThisPlan: (state, getters, rootState, rootGetters) => {
    const companyPlans = getters.contractedPlans;
    const planKey = rootGetters.selectedPlanKey;
    return !rootGetters.selectedPlanKey
      ? companyPlans.find((plan) => {
          if (plan.plan.key === planKey) {
            return plan;
          }
        })
      : false;
  },
  doNotHaveCNPJ: (state) => !state.company.cnpj,
  companyLogo: (state) => state.company.logo_url || false,
  companyBanner: (state) => state.company.banner_url || false,
  companyBannerLink: (state) => state.company.banner_link || "",
  plansInTrialToShowSchedule: (state) => {
    const companyPlans = [...state.plans];
    return companyPlans.find((companyPlan) => {
      const { plan, status } = companyPlan;
      return (
        [ENTERPRISE_SKU, BASIC_SKU].includes(plan.sku) && status == STATUS.TRIAL
      );
    });
  },

  hasBasicInTrial: (state) => {
    const companyPlans = [...state.plans];
    return companyPlans.find((companyPlan) => {
      const { plan, status } = companyPlan;
      return BASIC_SKU == plan.sku && status == STATUS.TRIAL;
    });
  },

  hasBasicFreeActive: (state) => {
    return state.plans.find(
      (plan) =>
        plan.status === "ACTIVE" &&
        plan.subscription_type === "FREE" &&
        plan.plan?.sku == BASIC_SKU
    );
  },

  showActionsScheduleDialog: (state) => {
    return state.actionsScheduleDialog;
  },

  showScheduleMeeting: (state, getters) => {
    const hasAnyCompanyPlanInTrialOrActive = getters.activeTrialPlans.length;
    return (
      !hasAnyCompanyPlanInTrialOrActive || !!getters.plansInTrialToShowSchedule
    );
  },

  hasConectaSign: (state) => {
    const companyPlans = state.plans || [];
    return companyPlans.find(
      (plan) =>
        plan.plan.products.find((product) => product.name === "conecta_sign") &&
        ["TRIAL", "ACTIVE"].includes(plan.status)
    )
      ? true
      : false;
  },
  hasSignAutomation: (state) => {
    const companyPlans = state.plans || [];
    return companyPlans.find(
      (plan) =>
        plan.plan.products.find(
          (product) => product.name === "sign_automation"
        ) && ["TRIAL", "ACTIVE"].includes(plan.valid_status)
    )
      ? true
      : false;
  },
  hasConectaControl: (state) => {
    const companyPlans = state.plans || [];
    return companyPlans.find(
      (plan) =>
        plan.plan.products.find(
          (product) => product.name === "conecta_control"
        ) && ["TRIAL", "ACTIVE"].includes(plan.valid_status)
    )
      ? true
      : false;
  },
  hasConectaTrack: (state) => {
    const companyPlans = state.plans || [];

    return companyPlans.find(
      (plan) =>
        plan.plan.products.find(
          (product) => product.name === "conecta_track"
        ) && ["TRIAL", "ACTIVE"].includes(plan.status)
    )
      ? true
      : false;
  },
  hasIntegracaoPontoMais: (state) => {
    const current_plan = state.company?.current_plan || {};

    return (
      !!current_plan.plan?.products.find(
        (product) => product.name === "integracao_ponto_mais"
      ) && ["TRIAL", "ACTIVE"].includes(current_plan.valid_status)
    );
  },
  organizationalUnits: (state) => state.uos,
  allOrgUnits: (state) => mergeChildren(state.uos),
  activeTrialPlans: (state) =>
    state.plans.reduce((acc, plan) => {
      if ((plan.status == "ACTIVE") | (plan.status == "TRIAL")) {
        acc.push(plan);
      }
      return acc;
    }, []),
  /**
   * Getter to catch all products available on a active plan
   */
  activeProducts: (state) =>
    state.plans
      .reduce((acc, plan) => {
        if (plan.status === "ACTIVE") {
          acc.push(...plan.plan.products);
        }
        return acc;
      }, [])
      .map((product) => product.name),
  /**
   * Getter to catch all active products available on a trial plan
   */
  ativeTrialProducts: (state) =>
    state.plans
      .reduce((acc, plan) => {
        if (plan.valid_status === STATUS.TRIAL) {
          acc.push(...plan.plan.products);
        }
        return acc;
      }, [])
      .map((item) => item.name),
  /**
   * Getter to catch all expired products available on a trial plan
   */
  disabledTrialProducts: (state) =>
    state.plans
      .reduce((acc, plan) => {
        if (plan.valid_status === STATUS.TRIAL_EXPIRED) {
          acc.push(...plan.plan.products);
        }
        return acc;
      }, [])
      .map((item) => item.name),
  companyServices: (state, getters) =>
    getters.contractedPlans.filter(
      (item) =>
        item.status === STATUS.DISABLED &&
        item.subscription_type === SUBSCRIPTIONS.ONCE
    ),
  hasEndedTrial: (state) => {
    const currentPlanStatus = state.company?.current_plan?.status;
    if (!currentPlanStatus) {
      return true;
    } else if (currentPlanStatus === "TRIAL") {
      return checkExpiredDate(state.company.current_plan.end_trial);
    }
    return false;
  },

  getUsedDomainsToCheckDNS: (state, rootGetters) => {
    const allUserDomains = rootGetters.allEmailsInCompany.map(
      (user) => user.email.split("@")[1]
    );
    const usedDomains = allUserDomains.filter(
      (domain, index) => allUserDomains.indexOf(domain) === index
    );

    return usedDomains;
  },

  functionalityClicked: (state) => state.functionalityClicked,

  companySharedDrives: (state) => state.companySharedDrives,

  domainLicenses: (state) => state.domainLicenses,
  loadingDomainLicenses: (state) => state.loadingDomainLicenses,

  currentCompanyPlan(state) {
    return state.company?.current_plan || {};
  },

  companyInTrial(_, { currentCompanyPlan }) {
    return currentCompanyPlan.valid_status === STATUS.TRIAL;
  },

  currentPlanIsBasicFree(_, { currentCompanyPlan }) {
    return currentCompanyPlan.subscription_type === SUBSCRIPTIONS.FREE;
  },

  isSalesChat(_, getters) {
    const { hasContractedPlans, company } = getters;
    return !hasContractedPlans || company.suspended;
  },

  postponeRoaming: (state) => state?.company.postpone_roaming,

  isSuiteClient(state, getters) {
    return state.company?.reseller_token === getters.conectaSuiteResellerToken;
  },
  isNuvemClient: (state, getters) => {
    return state.company?.reseller_token === getters.conectaNuvemResellerToken;
  },
  isPartnerClient: (state, getters) => {
    return !getters.isSuiteClient && !getters.isNuvemClient;
  },

  partnerHasSupportChatAvailable: (state) => {
    return state.company?.reseller_company?.is_support_chat_available;
  },

  hasSuiteSupport: (state, getters) => {
    return (
      getters.isSuiteClient ||
      (getters.isPartnerClient && getters.partnerHasSupportChatAvailable)
    );
  },

  hasNuvemSupport: (state, getters) => {
    return getters.isNuvemClient;
  },

  isNew: (state) => !state.company || !!state.company?.is_new,

  appIsInstalled: (state, getters) => {
    return (
      !!getters.currentUser?.id_company &&
      state.company?.app_is_installed !== "no"
    );
  },

  isMsRolesConfirmed: (state) =>
    state.msRolesConfirmed ||
    localStorage.getItem("companyMsRolesConfirmed") === "true",

  licenseLimiterSettings: (state) => state.company?.license_limiter_settings,
  licenseLimiterSettingsIsActive: (state) =>
    !!state.company?.license_limiter_settings?.is_active,
};

const actions = {
  syncUos({ commit, getters }) {
    if (getters.isCompanyMicrosoft) return;

    return this._vm.$axios
      .get(process.env.VUE_APP_API_BASE_URL + "/uos/sync", {
        headers: {
          Authorization: this._vm.token,
        },
      })
      .then(({ data }) => {
        commit("setUos", data);
      })
      .catch((error) => {
        console.error("Vuex: syncUos(): ", error);
      });
  },

  getUos({ commit, getters }) {
    if (getters.isCompanyMicrosoft) return;

    return this._vm.$axios
      .get(`${process.env.VUE_APP_API_BASE_URL}/uos`, {
        headers: {
          Authorization: this._vm.token,
        },
      })
      .then(({ data }) => {
        commit("setUos", data);
      })
      .catch((err) => {
        console.error("Vuex: getUos(): ", err);
      });
  },

  // Carrega os planos que o cliente tem
  async getContractedPlans({ commit }, reload = false) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/plans`;

    commit("startLoadingPlans");

    await this._vm.$axios
      .get(url, {
        headers: {
          Authorization: this._vm.token,
        },
      })
      .then(({ data }) => {
        commit("setContractedPlans", data);
        if (reload) {
          location.reload();
        }
      })
      .catch((e) => {
        console.error("Vuex: getContractedPlans()", e);
        commit("setContractedPlans");
      });
  },

  // TODO: descontinuar esse método e usar o método setSignatureTemplateType
  changeModelType({ getters }, template_id) {
    let url =
      process.env.VUE_APP_API_BASE_URL +
      `/conecta-sign/${template_id}/template-default`;

    let auth = {
      headers: {
        Authorization: getters.token,
      },
    };

    return this._vm.$axios.put(url, {}, auth);
  },

  changeVacationModelType({ getters }, template_id) {
    let url = `${process.env.VUE_APP_API_BASE_URL}/conecta-sign/vacation/templates/${template_id}/default`;

    let auth = {
      headers: {
        Authorization: getters.token,
      },
    };

    return this._vm.$axios.patch(url, {}, auth);
  },

  async getCompany({ commit, dispatch }) {
    let endpoint = process.env.VUE_APP_API_BASE_URL + "/company";

    await this._vm.$axios
      .get(endpoint, {
        headers: {
          Authorization: this._vm.token,
        },
      })
      .then(({ data }) => {
        if (data.is_banned) {
          dispatch("logoutWithDialog");
        }
        commit("setCompany", data);
        localStorage.setItem("companyOauth", data.oauth);

        if (
          [SUBSCRIPTIONS.ANNUAL, SUBSCRIPTIONS.FLEX].includes(
            data?.current_plan?.subscription_type
          )
        ) {
          commit(
            "setSelfCheckoutSubscriptionType",
            data.current_plan.subscription_type
          );
        }
      })
      .catch((err) => {
        console.error("Vuex: getCompany()", err);
      });
  },

  async getGoogleWorkspaceGroups({ commit, state }) {
    if (state.groups.length) {
      return;
    }

    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups`;

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    await this._vm.$axios
      .get(url, headers)
      .then(({ data }) => {
        commit("setGroups", data.group_emails);
      })
      .catch((err) => {
        console.error("Vuex: getGoogleWorkspaceGroups()", err);
      });
  },

  async createGoogleWorkspaceGroup({ commit }, group_settings) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups`;
    let group = {};

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    commit("setLoading", true);

    await this._vm.$axios
      .post(url, group_settings, headers)
      .then(({ data }) => {
        group = data;
        commit("setSnackBar", {
          message: `Grupo criado com sucesso.`,
          show: true,
          color: "success",
          timeout: 8000,
        });
      })
      .catch((err) => {
        console.error("Vuex: createGoogleWorkspaceGroup()", err);
        let message = "Ops, algo deu errado ao criar seu grupo.";
        if (err.response.request.status === 409) {
          message = "Ops, esse endereço de e-mail já está sendo utilizado.";
        }
        commit("setSnackBar", {
          message: message,
          show: true,
          color: "error",
          timeout: 8000,
        });
      })
      .finally(() => {
        commit("setLoading", false);
      });

    return group;
  },

  async updateGoogleWorkspaceGroup({ commit }, group_settings) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups`;
    let group = {};

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    commit("setLoading", true);

    await this._vm.$axios
      .put(url, group_settings, headers)
      .then(({ data }) => {
        group = data;
        commit("setSnackBar", {
          message: `Grupo atualizado com sucesso.`,
          show: true,
          color: "success",
          timeout: 8000,
        });
      })
      .catch((err) => {
        console.error("Vuex: updateGoogleWorkspaceGroup()", err);
        commit("setSnackBar", {
          message: "Ops, algo deu errado ao atualizar seu grupo.",
          show: true,
          color: "error",
          timeout: 8000,
        });
      })
      .finally(() => {
        commit("setLoading", false);
      });

    return group;
  },

  async getGroupMembers(_, group_email) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/group/members?group_email=${group_email}`;
    const auth = { headers: { Authorization: this._vm.token } };
    return await this._vm.$axios.get(url, auth);
  },

  async insertGroupOnSharedDrives(_, { payload }) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups/shared-drives`;
    const auth = { headers: { Authorization: this._vm.token } };
    return await this._vm.$axios.post(url, payload, auth);
  },

  async deletePermissionOnSharedDrives(_, payload) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/shared-drives/permission`;
    const auth = { headers: { Authorization: this._vm.token } };
    return await this._vm.$axios.delete(url, {
      ...auth,
      data: payload,
    });
  },

  async addGoogleWorkspaceGroupMember({ commit }, member_settings) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups-members`;
    let group_member = {};

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    commit("setLoading", true);

    await this._vm.$axios
      .post(url, member_settings, headers)
      .then(({ data }) => {
        group_member = data;
        commit("setSnackBar", {
          message: `Participante adicionado com sucesso.`,
          show: true,
          color: "success",
          timeout: 8000,
        });
      })
      .catch((err) => {
        console.error("Vuex: addGoogleWorkspaceGroupMember()", err);
        commit("setSnackBar", {
          message: "Ops, algo deu errado ao adicionar um participante.",
          show: true,
          color: "error",
          timeout: 8000,
        });
      })
      .finally(() => {
        commit("setLoading", false);
      });

    return group_member;
  },

  async updateGoogleWorkspaceGroupMember({ commit }, member_settings) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups-members`;
    let group_member = {};

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    commit("setLoading", true);

    await this._vm.$axios
      .put(url, member_settings, headers)
      .then(({ data }) => {
        group_member = data;
        commit("setSnackBar", {
          message: `Participante atualizado com sucesso.`,
          show: true,
          color: "success",
          timeout: 8000,
        });
      })
      .catch((err) => {
        console.error("Vuex: updateGoogleWorkspaceGroupMember()", err);
        commit("setSnackBar", {
          message: "Ops, algo deu errado ao atualizar um participante.",
          show: true,
          color: "error",
          timeout: 8000,
        });
      })
      .finally(() => {
        commit("setLoading", false);
      });

    return group_member;
  },

  async deleteGoogleWorkspaceGroupMember({ commit }, settings) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/groups-members`;

    const headers = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    commit("setLoading", true);

    await this._vm.$axios
      .delete(url, {
        ...headers,
        data: settings,
      })
      .then(() => {
        commit("setSnackBar", {
          message: `Participante removido com sucesso.`,
          show: true,
          color: "success",
          timeout: 8000,
        });
      })
      .catch((err) => {
        console.error("Vuex: deleteGoogleWorkspaceGroupMember()", err);
        commit("setSnackBar", {
          message: "Ops, algo deu errado ao remover um participante.",
          show: true,
          color: "error",
          timeout: 8000,
        });
      })
      .finally(() => {
        commit("setLoading", false);
      });
  },

  async verifyControlCompanyStatus({ commit, state }) {
    const endpoint =
      "https://us-central1-access-control2.cloudfunctions.net/verify-domain-on-access-control";
    const query_params =
      "?token=CHdjksW$$d23hjs&domain=" + state.company.main_domain;
    const url = endpoint + query_params;

    await this._vm.$axios
      .get(url)
      .then(({ data }) => {
        const status = data.status;
        commit("setControlStatusClient", status);
      })
      .catch((err) => {
        commit("setControlStatusClient", null);
        console.error("Vuex: verifyControlCompanyStatus()", err);
      });
  },

  checkHasProductPermissions({ state, getters }, product_name) {
    const currentPlan = state.company.current_plan;
    const user = getters.currentUser;
    const permission = user.permission[product_name];

    const hasProduct = currentPlan.plan?.products.some(
      (product) => product.name === product_name
    );

    const hasValidStatus = ["ACTIVE", "TRIAL"].includes(
      currentPlan.valid_status
    );

    if (currentPlan.valid_status === "TRIAL_EXPIRED") {
      return { code: "PLAN_TRIAL_ENDED", hasPermission: false };
    }

    if (!hasValidStatus) {
      return { code: "PRODUCT_IN_DISABLED_PLAN", hasPermission: false };
    }

    if (!hasProduct) {
      return { code: "PRODUCT_DOES_NOT_EXIST_IN_PLANS", hasPermission: false };
    }

    if (product_name === "conecta_control") {
      return { code: "SUCCESS", hasPermission: true }; // verificação de permissão de usuário no control é feito no back
    }

    if (!(user.is_google_admin || permission === "admin")) {
      return { code: "USER_IS_NOT_ADMIN", hasPermission: false };
    }

    return { code: "SUCCESS", hasPermission: true };
  },

  async checkHasTaskRunningByCompany({ getters, dispatch }) {
    if (!getters.company?.key) return;
    if (getters.hasConectaControl && !getters.migrationsHistory.length) {
      await dispatch("syncMigrationsHistory");
    }

    const db = getDatabase();
    const hasTaskRunning = await get(
      ref(
        db,
        `task_processes/${getters.company.key}/email_to_group/has_task_running`
      )
    );

    const hasMigrationWaiting = getters.migrationsHistory.filter((migration) =>
      ["IN_QUEUE", "IN_PROGRESS"].includes(migration.status)
    ).length;

    return !!hasTaskRunning.val() || !!hasMigrationWaiting;
  },

  async purchaseInterest(ctx, product) {
    const url =
      process.env.VUE_APP_API_BASE_URL +
      `/company-plan/${product}/purchase-interest`;

    return await this._vm.$axios.post(
      url,
      {},
      {
        headers: { Authorization: this._vm.token },
      }
    );
  },

  startHubpostIntegration({ getters, commit }) {
    window.hsConversationsOnReady = [
      () => {
        window.HubSpotConversations.on("widgetLoaded", () => {
          commit("setChatLoading", false);
          commit("setChatWidgetOpened", true);
        });
        window.HubSpotConversations.on("widgetClosed", () => {
          commit("setSupportChatOpen", false);
          commit("setChatWidgetOpened", false);
          removeHubSpotChat();
        });
      },
    ];
    const projectId = getters.isNuvemClient
      ? process.env.VUE_APP_HUBSPOT_NUVEM_PROJECT_ID
      : process.env.VUE_APP_HUBSPOT_SUITE_PROJECT_ID;

    loadHubspotScript(projectId);
    identifyUserOnHubSpot(getters.currentUser);
  },

  async initSupportChat({ getters, commit, dispatch }) {
    if (getters.chatLoading) {
      return;
    }

    commit("setChatLoading", true);

    if (getters.hasNuvemSupport || getters.hasSuiteSupport) {
      commit("setFloatingGuide", false);
      commit("setSupportChatOpen", true);
      await dispatch("verifyHubspotToken");
      return;
    }
    commit("setChatLoading", false);
    commit("setOpenModalSupport", true);
  },

  async verifyHubspotToken({ getters, commit, dispatch }) {
    if (getters.isChatWidgetOpened) {
      commit("setChatLoading", false);
      return;
    }

    if (handleDisabledHubspotChat(commit)) return;

    let hubToken = JSON.parse(localStorage.getItem("hubspotToken"));

    if (!hubToken || isTokenExpired(hubToken)) {
      hubToken = await dispatch("getUserHubSpotToken");
      localStorage.setItem("hubspotToken", JSON.stringify(hubToken));
    }

    const { currentUser, isSalesChat } = getters;

    await handleHubspotChatLoad(currentUser.email, hubToken, isSalesChat);
    if (handleDisabledHubspotChat(commit)) return;
  },

  getScopes({ commit }) {
    let urlSuite = process.env.VUE_APP_API_BASE_URL + "/client/scopes";

    this._vm.$axios
      .get(urlSuite, {
        headers: { Authorization: this._vm.token },
      })
      .then(({ data }) => {
        commit("setMissingScopesSuite", data.missing_scopes);
      })
      .catch(() => commit("setMissingScopesSuite", []));
  },

  getCompanyAddressByCnpj(ctx, cnpj) {
    const url = process.env.VUE_APP_CLOUD_FUNCTION_SEARCH_CNPJ;
    return this._vm.$axios.post(url, { cnpj });
  },

  async functionalityInterest({ state }, { product, functionality }) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/functionality-interest`;

    return await this._vm.$axios.post(
      url,
      {
        product,
        functionality: state.functionalityClicked || functionality,
      },
      {
        headers: { Authorization: this._vm.token },
      }
    );
  },
  async getCompanySharedDrives({ commit, getters }) {
    if (getters.isCompanyMicrosoft) return;

    const url = process.env.VUE_APP_API_BASE_URL + "/company/shared-drives";

    await this._vm.$axios
      .get(url, {
        headers: {
          Authorization: this._vm.token,
        },
      })
      .then(({ data }) => {
        commit("setCompanySharedDrives", data);
        return;
      })
      .catch((err) => {
        console.error("Vuex: getCompanySharedDrives()", err);
        return err.response.data.code;
      });
  },

  checkRunningTasks() {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/has-task-running`;
    const auth = {
      headers: { Authorization: this._vm.token },
    };
    return this._vm.$axios.get(url, auth);
  },

  async isAppInstalled({ commit, dispatch, rootState }) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/installed`;

    if (!rootState.hutk) {
      await dispatch("fetchHutk", null, { root: true });
    }
    const hutk = rootState.hutk;

    const auth = {
      headers: {
        Authorization: this._vm.token,
      },
    };

    if (!auth.params) auth.params = {};
    auth.params.hutk = hutk;

    return this._vm.$axios
      .get(url, auth)
      .then(async (response) => {
        commit("setCompany", response.data.company);
        commit("setCurrentUser", response.data.user);

        dispatch("getSyncUsersStatus", response.data.company);
        commit("setToken", response.data.token);
        await dispatch("getContractedPlans");

        return true;
      })
      .catch((error) => {
        console.error("Vuex: isAppInstalled(): ", error);

        return false;
      });
  },

  async updateVerifiedPhone(
    { rootGetters },
    { phoneNumber, status_code, message = "" }
  ) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/verify`;
    const auth = {
      headers: {
        Authorization: rootGetters.token,
      },
    };
    const verified_phone = "+" + removeMask("phone", phoneNumber);

    return await this._vm.$axios
      .put(url, { verified_phone, status_code, message }, auth)
      .catch((error) => {
        console.error("updateVerifiedPhone()", error);
      });
  },

  async ensureMsRoles({ commit }) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/ensure-roles-ms`;
    try {
      await this._vm.$axios.post(
        url,
        {},
        {
          headers: { Authorization: this._vm.token },
        }
      );
      commit("setMsRolesConfirmed", true);
      localStorage.setItem("companyMsRolesConfirmed", "true");
      return true;
    } catch (error) {
      console.error("Vuex: ensureMsRoles()", error);
      commit("setMsRolesConfirmed", false);
      localStorage.removeItem("companyMsRolesConfirmed");
      return false;
    }
  },

  async getDomainLicenses({ getters, commit }) {
    commit("setLoadingDomainLicenses", true);

    const url = `${process.env.VUE_APP_API_BASE_URL}/license-limiter`;

    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      const response = await this._vm.$axios.get(url, config);
      commit("setDomainLicenses", response.data);
    } catch (error) {
      console.error("Vuex: getDomainLicenses()", error);
    } finally {
      commit("setLoadingDomainLicenses", false);
    }

    return getters.domainLicenses;
  },

  async updateDomainLicensesLimit(_, payload) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/license-limiter`;
    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      const response = await this._vm.$axios.patch(url, payload, config);
      return response.data;
    } catch (error) {
      console.error("Vuex: updateDomainLicensesLimit()", error);
      throw error;
    }
  },

  async updateLicenseLimiterSettings({ commit }, payload) {
    const url = `${process.env.VUE_APP_API_BASE_URL}/license-limiter/settings`;
    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      const response = await this._vm.$axios.patch(url, payload, config);
      commit("setCompany", response.data);
      return response.data;
    } catch (error) {
      console.error("Vuex: updateLicenseLimiterSettings()", error);
      throw error;
    }
  },

  async syncCompanyLicenses() {
    const url = `${process.env.VUE_APP_API_BASE_URL}/company/sync-license`;
    const config = {
      headers: { Authorization: this._vm.token },
    };

    try {
      await this._vm.$axios.post(url, {}, config);
    } catch (error) {
      console.error("Vuex: syncCompanyLicenses()", error);
      throw error;
    }
  },
};

const mutations = {
  setUos(state, uos) {
    state.uos = uos;
  },
  startLoadingPlans(state) {
    state.loadingClientPlans = true;
  },
  setContractedProducts(state, products) {
    state.products = products;
  },
  setContractedPlans(state, plans = []) {
    state.plans = plans;
    state.loadingClientPlans = false;
    state.loadedClientPlans = true;
  },
  setControlStatusClient(state, status) {
    state.control = status;
  },

  setCompany(state, company) {
    state.company = company;
  },

  setGroups(state, groups = []) {
    state.groups = groups;
  },

  setMissingScopesSuite(state, missingScopesSuite) {
    state.missingScopesSuite = missingScopesSuite;
  },

  setNewUserSignModel(state, templateId) {
    state.company.new_users_signature = templateId;
  },

  SET_MEETING_VISIBILITY(state, status = false) {
    state.IsScheduleMeetingSnackbarVisible = status;
  },

  setActionScheduleDialog(state, config) {
    const { status = false, type = "help" } = config;

    state.actionsScheduleDialog = status;
    state.actionsScheduleType = type;
  },

  setFunctionalityClicked(state, functionality) {
    state.functionalityClicked = functionality;
  },

  setCompanySharedDrives(state, data) {
    state.companySharedDrives = data;
  },

  setCompanyPostponeRoaming(state, data) {
    state.company.postpone_roaming = data;
  },

  setOpenModalSupport(state, visible) {
    state.openModalSupport = visible;
  },

  setMsRolesConfirmed(state, value) {
    state.msRolesConfirmed = value;
  },

  setDomainLicenses(
    state,
    domainLicenses = { items: {}, header_licenses: [] }
  ) {
    state.domainLicenses = domainLicenses;
  },

  setLoadingDomainLicenses(state, value) {
    state.loadingDomainLicenses = value;
  },
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
